/**
 * RS / Header — menu panel controller.
 *
 * The panel is a top sheet that opens under the header bar (the bar stays
 * visible and the MENU button toggles + / −, as in the prototype). While
 * open: focus is trapped inside the panel, Esc and clicks outside close it,
 * page scroll is locked, and closing restores focus to the MENU button.
 *
 * No scroll listeners anywhere — the fixed header is pure CSS (position:
 * sticky on the section wrapper).
 */
const FOCUSABLE = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';

/* Anti-overlap: minimum clearance between the centered nav and each side,
   plus hysteresis so the layout never flaps at the threshold. */
const SIDE_CLEARANCE_PX = 24;
const COLLAPSE_SLACK_PX = 24;
const EXPAND_SLACK_PX = 72;
const ACCOUNT_ICON_PX = 44;

class RsHeader extends HTMLElement {
  #open = false;
  #collapsed = false;

  connectedCallback() {
    this.toggleBtn = this.querySelector('[data-rs-menu-toggle]');
    this.chevron = this.querySelector('[data-rs-chevron]');
    this.panel = this.querySelector('[data-rs-panel]');
    if (!this.toggleBtn || !this.panel) return;

    this.#setupCollapse();

    this.toggleBtn.addEventListener('click', () => (this.#open ? this.#close() : this.#openPanel()));

    // Entries that hand off to another surface (search modal) close the panel
    // WITHOUT sending focus back to the MENU button: focus belongs to the
    // surface being opened, and the programmatic focus was drawing a ring
    // around MENU on mobile
    for (const el of this.panel.querySelectorAll('[data-rs-close-panel]')) {
      el.addEventListener('click', () => this.#unlock());
    }

    this.onDocClick = (event) => {
      if (!this.#open) return;
      if (this.panel.contains(event.target) || this.toggleBtn.contains(event.target)) return;
      this.#close();
    };
    this.onKeydown = (event) => {
      if (!this.#open) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.#close();
        return;
      }
      if (event.key !== 'Tab') return;

      // Focus trap: cycle within the panel
      const focusables = Array.from(this.panel.querySelectorAll(FOCUSABLE));
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!this.panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    };
  }

  disconnectedCallback() {
    if (this.#open) this.#unlock();
    this.resizeObserver?.disconnect();
  }

  /* Content-aware mobile switch: collapse BEFORE the centered nav can touch
     the logo or the right cluster, whatever their actual widths are. */
  #setupCollapse() {
    this.bar = this.querySelector('.rs-header__bar');
    this.logo = this.querySelector('.rs-header__logo');
    this.nav = this.querySelector('.rs-header__nav');
    this.actions = this.querySelector('.rs-header__actions');
    this.accountAction = this.actions?.querySelector('.rs-header__account') ?? null;
    if (!this.bar || !this.logo) return;

    this.logoIsImage = !!this.logo.querySelector('.rs-header__logo-image');
    const styles = this.getAttribute('style') || '';
    this.logoDesktopH = parseFloat((styles.match(/--rs-logo-h:\s*([\d.]+)/) || [])[1]) || 0;
    this.logoMobileH = parseFloat((styles.match(/--rs-logo-h-mobile:\s*([\d.]+)/) || [])[1]) || 0;

    this.setAttribute('data-rs-measured', '');
    // First measurement: no hysteresis yet — collapsing needs the FULL comfort
    // margin, so intermediate widths never land in the dead zone where the
    // bar stays expanded but too tight (centered nav covering the logo)
    this.#updateCollapse(true);

    this.resizeObserver = new ResizeObserver(() => this.#updateCollapse());
    this.resizeObserver.observe(this.bar);
    if (this.nav) this.resizeObserver.observe(this.nav);
    this.resizeObserver.observe(this.logo);
  }

  #updateCollapse(initial = false) {
    // Published at :root: top-layer surfaces (search dialog) can't reach the
    // group-scoped header height var, but must start below the real header
    document.documentElement.style.setProperty(
      '--rs-header-bottom',
      `${Math.round(this.bar.getBoundingClientRect().bottom)}px`
    );
    const barWidth = this.bar.clientWidth;
    const navWidth = this.nav ? this.nav.offsetWidth : 0;
    let logoWidth = this.logo.offsetWidth;

    // While collapsed, the image logo renders at its mobile height — scale the
    // measurement back to desktop size so the expansion estimate is honest
    if (this.#collapsed && this.logoIsImage && this.logoMobileH > 0 && this.logoDesktopH > 0) {
      logoWidth = (logoWidth * this.logoDesktopH) / this.logoMobileH;
    }

    let rightWidth = this.toggleBtn.offsetWidth + (this.actions ? this.actions.offsetWidth : 0);
    if (this.#collapsed && this.accountAction) rightWidth += ACCOUNT_ICON_PX;

    // Centered nav: each side must fit the wider of logo / right cluster
    const needed = navWidth + 2 * Math.max(logoWidth, rightWidth) + 2 * SIDE_CLEARANCE_PX;

    const collapseSlack = initial ? EXPAND_SLACK_PX : COLLAPSE_SLACK_PX;
    if (!this.#collapsed && barWidth < needed + collapseSlack) {
      this.#setCollapsed(true);
    } else if (this.#collapsed && barWidth > needed + EXPAND_SLACK_PX) {
      this.#setCollapsed(false);
    }
  }

  #setCollapsed(collapsed) {
    this.#collapsed = collapsed;
    this.setAttribute('data-rs-collapsed', collapsed ? 'true' : 'false');
    this.nav?.setAttribute('aria-hidden', collapsed ? 'true' : 'false');
  }

  #openPanel() {
    this.#open = true;
    this.panel.hidden = false;
    this.toggleBtn.setAttribute('aria-expanded', 'true');
    if (this.chevron) this.chevron.textContent = '−';
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onKeydown);
    // Focus the panel itself, not its first link: focusing HOME directly drew
    // a visible focus ring around it on every open. The trap still works —
    // Tab enters the links, Esc/outside-click close, and the panel swallows
    // its own focus outline
    this.panel.focus();
  }

  #close() {
    if (!this.#open) return;
    this.#unlock();
    this.toggleBtn.focus();
  }

  #unlock() {
    this.#open = false;
    this.panel.hidden = true;
    this.toggleBtn.setAttribute('aria-expanded', 'false');
    if (this.chevron) this.chevron.textContent = '+';
    document.documentElement.style.overflow = '';
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onKeydown);
  }
}

if (!customElements.get('rs-header')) {
  customElements.define('rs-header', RsHeader);
}
